import i18next from 'i18next';
import { defaultTo, isEmpty, isNil, get } from 'lodash';
import { getRoot } from 'mobx-easy';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { LogRocketService } from '@bringg-frontend/bringg-web-infra';

import ServiceAreaViewStore from 'bringg-web/stores/service-areas-view/service-areas-view-store';
import RootStore from './root-store';
import CrossAppDynamicFormModalView from '../cross-application/modals/dynamic-form/cross-app-dynamic-form-modal-view';
import { registerAction } from '../services/cross-application/cross-application';
import { UserSettingsAction } from '../services/cross-application/cross-application.actions';
import { AuthStore } from './auth-store/auth-store';
import { hasFeatureFlag } from '../utils/feature-flags';

const UNIFIED_LANGUAGE_FALLBACK_FLAG = 'enable_unified_language_fallback';
const DEFAULT_LANGUAGE_CODE = 'en';

const requestAngularLocale = (): string | undefined => {
	const { crossAppTransport } = getRootEnv().dashboardSdk.sdk;
	const [angularLocale] = crossAppTransport.emit(UserSettingsAction.GET_RESOLVED_LOCALE);

	return angularLocale;
};

class ViewsStore {
	authStore: AuthStore;
	crossAppDynamicFormModalView: CrossAppDynamicFormModalView;
	serviceAreaViewStore: ServiceAreaViewStore;

	constructor() {
		this.authStore = new AuthStore();
		this.crossAppDynamicFormModalView = new CrossAppDynamicFormModalView();
		this.serviceAreaViewStore = new ServiceAreaViewStore();
	}

	syncLanguageWithAngular() {
		this.initLanguage();

		registerAction(
			UserSettingsAction.USER_SETTINGS_UPDATE_LANG,
			this.initLanguage,
			getRootEnv().dashboardSdk.sdk.crossAppTransport
		);
	}

	initLanguage = (languageCode?: string) => {
		const { firstDayOfWeek } = getRoot<RootStore>().data.merchantConfigurationsStore;
		const resolvedLanguageCode = this.resolveLanguageCode(languageCode);

		if (!isEmpty(resolvedLanguageCode)) {
			i18next.changeLanguage(resolvedLanguageCode);
			const env = getRootEnv();
			env.localeService.initMomentLocale(resolvedLanguageCode, firstDayOfWeek);
			getRoot<RootStore>().data.usersStore.setCurrentUserLanguage(resolvedLanguageCode);
		}
	};

	private resolveLanguageCode(pushedLanguageCode?: string): string {
		const { user } = getRootEnv().dashboardSdk.sdk.session;

		if (!hasFeatureFlag(user, UNIFIED_LANGUAGE_FALLBACK_FLAG)) {
			const merchantLanguageCode =
				getRoot<RootStore>().data.merchantConfigurationsStore.defaultDashboardLanguageCode;

			return defaultTo(pushedLanguageCode || user.language, merchantLanguageCode);
		}

		if (!isEmpty(user.language)) {
			return user.language;
		}

		const angularLanguageCode = pushedLanguageCode || requestAngularLocale();

		if (!isEmpty(angularLanguageCode)) {
			return angularLanguageCode;
		}

		return DEFAULT_LANGUAGE_CODE;
	}

	initLogRocket() {
		const appId = getRootEnv().envConfig.log_rocket_app_id;
		const user = get(getRootEnv(), 'dashboardSdk.sdk.session.user');

		if (hasFeatureFlag(user, 'enable_log_rocket') && !isNil(appId)) {
			LogRocketService.initLogRocket(appId, user)
				// eslint-disable-next-line no-console
				.then(() => console.log('LogRocket initialized successfully'))
				.catch(e => console.error('LogRocket initialization failed', e));
		}
	}
}

export default ViewsStore;
